/**
 * ClinicAccessGrants.jsx — SuperAdmin-only "Temporary clinic access" panel.
 *
 * Grants WIDEN one user's clinic access additively and temporarily: they can
 * see/act on the added clinic(s) ON TOP of whatever their role already gives,
 * until an expiry date, after which the grant auto-ignores (no role change, no
 * revoke step required). Grants are CLINICS-ONLY and never restrict.
 *
 * The grant list is the governed `clinicAccessGrants` config key — saved via the
 * standard governed updateConfig({ publish: true }) flow (SuperAdmin-only both in
 * the UI here and server-side in canWriteConfigKey). Enforcement lives entirely
 * in api/src/lib/scope.js (extraClinics); this panel only authors the list.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getTherapieMapsData } from '../api/blobClient.js';
import { isStaffEmail, STAFF_EMAIL_DOMAINS } from '@shared/notificationSchema.js';
import { validateClinicAccessGrants } from '@shared/configSchema.js';
import SearchableSelect from './SearchableSelect.jsx';
import StaffEmailInput from './StaffEmailInput.jsx';

const CONFIG_KEY = 'clinicAccessGrants';
const DEFAULT_DAYS = 30;
// Mirror the server cap (MAX_GRANT_DAYS in configSchema.js) — grants are temporary.
const MAX_DAYS = 90;

const uniq = (arr) => [...new Set(arr.filter(Boolean))];
const freshId = () => 'grant-' + Math.random().toString(36).slice(2, 10);

/** YYYY-MM-DD for `d` days from now (local), for the date input default. */
function isoDatePlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

/** A YYYY-MM-DD picker value → an end-of-day UTC ISO instant, so the grant is
 *  live through the whole chosen day. */
function dayEndIso(dateStr) {
  if (!dateStr) return '';
  const t = Date.parse(`${dateStr}T23:59:59.999Z`);
  return Number.isFinite(t) ? new Date(t).toISOString() : '';
}

function isExpired(grant, now = Date.now()) {
  const t = Date.parse(grant?.expiresAt);
  return !Number.isFinite(t) || t <= now;
}

function fmtDate(iso) {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString() : '—';
}

export default function ClinicAccessGrants({ config, onPublish, embedded = false, filter = '' }) {
  const grants = Array.isArray(config?.clinicAccessGrants) ? config.clinicAccessGrants : [];

  const [tmData, setTmData]   = useState(null);
  const [email, setEmail]     = useState('');
  const [clinics, setClinics] = useState([]);
  const [expiry, setExpiry]   = useState(() => isoDatePlusDays(DEFAULT_DAYS));
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    getTherapieMapsData().then(d => { if (!cancelled) setTmData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const clinicOptions = useMemo(
    () => uniq((tmData?.clinics || []).map(c => c.name)).sort(),
    [tmData],
  );

  const todayIso = new Date().toISOString().split('T')[0];
  const maxIso   = isoDatePlusDays(MAX_DAYS);        // grants can't run longer than this
  const emailOk  = isStaffEmail(email.trim());
  const expiryOk = !!expiry && expiry > todayIso && expiry <= maxIso;  // FUTURE, within the cap
  const canSave  = emailOk && clinics.length > 0 && expiryOk && !busy;

  function resetForm() {
    setEmail(''); setClinics([]); setExpiry(isoDatePlusDays(DEFAULT_DAYS)); setNote('');
  }

  async function publish(nextGrants, successMsg) {
    // Client-side echo of the server validator so an obviously-bad list never
    // even hits the API (the server re-validates authoritatively regardless).
    const check = validateClinicAccessGrants(nextGrants);
    if (!check.ok) { setError(check.errors[0] || 'That grant is not valid.'); return false; }
    setBusy(true); setError(null); setNotice(null);
    try {
      await onPublish(CONFIG_KEY, nextGrants, { publish: true });
      setNotice(successMsg);
      return true;
    } catch (e) {
      setError(e.message || 'Save failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function addGrant(e) {
    e?.preventDefault?.();
    if (!canSave) return;
    const grant = {
      id: freshId(),
      email: email.trim().toLowerCase(),
      clinics: [...clinics],
      expiresAt: dayEndIso(expiry),
      grantedBy: null,   // stamped server-side context is the audit trail; UI leaves null
      grantedAt: new Date().toISOString(),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    const ok = await publish([...grants, grant], 'Access granted.');
    if (ok) resetForm();
  }

  async function revoke(id) {
    const g = grants.find(x => x.id === id);
    if (g && !window.confirm(`Revoke ${g.email}'s access to ${g.clinics.join(', ')}?`)) return;
    await publish(grants.filter(x => x.id !== id), 'Grant removed.');
  }

  const sorted = [...grants].sort((a, b) => {
    const ea = isExpired(a), eb = isExpired(b);
    if (ea !== eb) return ea ? 1 : -1;                    // active first
    return Date.parse(a.expiresAt || 0) - Date.parse(b.expiresAt || 0);
  });
  // Host view's email filter (when embedded) narrows the list.
  const fq = String(filter || '').trim().toLowerCase();
  const shown = fq ? sorted.filter(g => (g.email || '').toLowerCase().includes(fq)) : sorted;

  return (
    <div>
      {/* Embedded inside Users & access the section head (title + desc) is
          provided by the host view — only render it standalone. */}
      {!embedded && (
        <div className="admin-section-head">
          <div>
            <div className="admin-section-title">Temporary clinic access</div>
            <div className="admin-section-desc">
              Grant a user access to <strong>additional</strong> clinic(s) on top of their normal role, with a
              required expiry date that auto-revokes — no role change needed. Additive only: this can widen a
              person's clinic access but never restrict it.
            </div>
          </div>
        </div>
      )}

      {error && <div className="callout danger" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>}
      {notice && <div className="callout" style={{ marginBottom: 12, fontSize: 12 }}>{notice}</div>}

      {/* ── Grant form ── */}
      <form onSubmit={addGrant} style={{
        display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18,
        padding: '14px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)',
      }}>
        {/* One row when there's space: email · expires · clinics (wraps when narrow). */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 240px', marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>User email</label>
            <StaffEmailInput
              value={email} directory
              onChange={v => { setEmail(v); setError(null); }}
              inputStyle={{ fontSize: 13 }}
              selectStyle={{ fontSize: 13 }}
            />
            {email.trim() && !emailOk && (
              <div className="field-hint" style={{ color: 'var(--danger)' }}>
                Must be a staff address ({STAFF_EMAIL_DOMAINS.join(' / ')}).
              </div>
            )}
          </div>
          <div className="field" style={{ width: 150, marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Expires</label>
            <input
              className="input" type="date" value={expiry} min={todayIso} max={maxIso}
              onChange={e => { setExpiry(e.target.value); setError(null); }}
              style={{ fontSize: 13 }} aria-label="Expiry date"
            />
            {expiry && !expiryOk && (
              <div className="field-hint" style={{ color: 'var(--danger)' }}>Pick a future date.</div>
            )}
          </div>
          <div className="field" style={{ flex: '1 1 280px', marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Additional clinic(s)</label>
            <SearchableSelect
              name="clinics"
              multiple
              value={clinics}
              onChange={e => { setClinics(e.target.value); setError(null); }}
              options={clinicOptions.map(c => ({ value: c, label: c }))}
              placeholder="Pick one or more clinics…"
            />
            {!tmData && <div className="field-hint">Clinic directory not loaded — the list may be incomplete.</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: '1 1 260px', marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Note (optional)</label>
            <input
              className="input" type="text" value={note} maxLength={300}
              placeholder="e.g. covering annual leave"
              onChange={e => setNote(e.target.value)}
              style={{ fontSize: 13 }} aria-label="Note"
            />
          </div>
          <button className="btn primary" type="submit" disabled={!canSave} style={{ fontSize: 12, flexShrink: 0 }}>
            {busy ? 'Saving…' : 'Grant access'}
          </button>
        </div>
      </form>

      {/* ── Existing grants ── */}
      <div className="admin-section-title" style={{ fontSize: 13, marginBottom: 8 }}>
        Current grants{grants.length ? (fq ? ` (${shown.length} of ${grants.length})` : ` (${grants.length})`) : ''}
      </div>
      {shown.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 0' }}>
          {fq && grants.length ? 'No grants match.' : 'No temporary clinic access grants.'}
        </div>
      ) : (
        <div className="admin-stack-sm">
          {shown.map(g => {
            const expired = isExpired(g);
            return (
              <div key={g.id} className={`admin-list-item${expired ? ' is-disabled' : ''}`}>
                <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', minWidth: 180 }}>{g.email}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>
                  {(g.clinics || []).join(', ')}
                  {g.note ? ` · ${g.note}` : ''}
                </span>
                <span style={{ fontSize: 11, color: expired ? 'var(--danger)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>
                  {expired ? 'Expired' : 'Expires'} {fmtDate(g.expiresAt)}
                </span>
                <button className="btn ghost sm" onClick={() => revoke(g.id)} disabled={busy}
                        style={{ fontSize: 11, color: 'var(--danger)' }}>
                  {expired ? 'Remove' : 'Revoke'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
